import { HttpResponse, Code } from '@/models/http'
import axiosRetry from 'axios-retry'
import axios from 'axios'
import { StorageKey } from '@/models/enums'
import { apiConfig, manage, IS_DEV } from '@/config/api'
import { i18n } from '@/i18n'
import { UserTokens } from '@/models/login'
import { useGlobalStore } from '@/store/global'
import { useUserStore } from '@/store/user'
import MPopup from '@/components/common/popup/m-popup.vue'

const { protocol, host, port, AERIAL_PREFIX } = apiConfig
const ApiServerHost = IS_DEV ? `/api` : `${protocol}://${host}:${port}${AERIAL_PREFIX}`
const apiWhitelist = [
  '/auth/wx/mplogin',
  '/auth/social/login',
  '/auth/wx/mpConfigInfo',
  '/journey/spot/home',
  '/journey/hotspot/home',
  '/journey/hotspot/app/home',
  '/journey/skypath/home',
  '/journey/journey/app/openFindUrl',
  '/system/social/qrCode',
  '/journey/hotspot/app/pageHotspot',
  '/journey/airline/home',
  '/smsCode/sendSmsCode',
  '/smsCode/login',
  '/journey/memory/detailForH5',
  '/mssom/dynamic/appShowState',
]

const specialApiWhitelist = [
  '/journey/nearbyAirlineTicket/userAirlineTicket',
  '/journey/dynamic/pageDynamic',
]

function createHttpClient(options: {
  buildRequestData: (data: any) => any
  unwrapResponseData: (data: HttpResponse) => any
}) {
  const instance = axios.create({
    baseURL: ApiServerHost,
    timeout: 30000,
    headers: {
      'x-mss-os': 'Android, iOS',
      'x-mss-timestamp': new Date().toISOString(),
      'x-mss-lang': i18n.global.locale.value,
    },
  })

  instance.interceptors.request.use(
    (config) => {
      const gStore = useGlobalStore()
      const userStore = useUserStore()
      const usertokens = userStore.userInfo.userTokens.access_token
        ? userStore.userInfo.userTokens
        : (storage.getItem(StorageKey.USER_TOKENS) as UserTokens | undefined)
      const platformInfo = gStore.platformInfo
      config.headers['clientid'] = usertokens?.client_id || ''
      config.headers['x-mss-shell'] = platformInfo.shell
      config.headers['x-mss-system'] = platformInfo.system
      config.headers['x-mss-clientInformation'] = gStore.clientInfo
      config.headers['x-mss-timestamp'] = new Date().toISOString()
      config.headers['x-mss-lang'] = i18n.global.locale.value
      if (config.method !== 'get') {
        config.headers['content-type'] = 'application/json'
        config.headers['accept'] = 'application/json'
      }
      if (config.method === 'post') {
        config.data = options.buildRequestData(config.data)
      }
      if (
        apiWhitelist.some((w) => {
          return config.url && config.url.includes(w)
        })
      ) {
        config.__url = config.url
        return config
      }
      if (
        config.__url &&
        apiWhitelist.some((w) => {
          return config.__url && config.__url.includes(w)
        })
      ) {
        return config
      }

      if (!usertokens?.access_token && specialApiWhitelist.some((w) => {
        return config.url && config.url.includes(w)
      })) {
        return config
      }

      if (!usertokens?.access_token) {
        return Promise.reject({
          code: 'ERR_UNKNOWN',
          message: 'no token',
          data: '',
          timestamp: new Date().toISOString(),
        })
      }
      config.headers!['Authorization'] = 'Bearer ' + usertokens.access_token
      return config
    },
    async (error) => {
      console.error('Request ERROR', error)
    },
  )

  instance.interceptors.response.use(
    (response) => {
      if (response) {
        const data = response.data as HttpResponse
        const code = data.code
        if (code != 200) {
          handleTip(code)
          return Promise.reject(data)
        } else {
          return options.unwrapResponseData(data)
        }
      } else {
        return Promise.reject({
          code: 'ERR_UNKNOWN',
          message: '未知错误',
          data: '',
          timestamp: new Date().toISOString(),
        })
      }
    },
    async (error) => {
      if (error.response) {
        return Promise.reject({
          code: error.code,
          data: error.response.data,
          message: error.message,
          timestamp: new Date().toISOString(),
        })
      } else {
        return Promise.reject(error)
      }
    },
  )

  return instance
}

const base = createHttpClient({
  buildRequestData: (data) => ({ timestamp: new Date().toISOString(), ...data }),
  unwrapResponseData: (data) => (!isEmpty(data.data) ? data.data : {}),
})
const baseV2 = createHttpClient({
  buildRequestData: (data) => ({ ...data, timestamp: new Date().toISOString() }),
  unwrapResponseData: (data) => (hasOwn(data, 'rows') ? data : data.data) || {},
})

// 只对幂等方法重试。
//
// axios-retry 的默认 retryCondition 是 isNetworkOrIdempotentRequestError，它等于
//   isNetworkError(error) || isIdempotentRequestError(error)
// 第一个分支**完全不看请求方法** —— 也就是说弱网下拿不到响应时，POST 也会被
// 自动重发。本仓所有写操作都是 POST：下单(schedule)、支付宝下单(aliPay/schedule)、
// 改签(ticketRebook)、兑换码下单(code/schedule)、订单创建(order/create)、
// 核销(order/check-in)、退款(refund/app/refunds)、退款审核、取消兑换、
// 模板支付(payment/airline/templatePay)、解锁订单(deLockOrder/freeOrderLock)。
// 其中「下单发出去没回应 → 自动重发 → 后端各建一单」就是 h5#7 记的重复下单，
// 而且重试发生在 HTTP 拦截器层，业务层的并发守卫拦不住它。
//
// 首页改版把「立即航拍」提到首屏一键可达，这条路径的曝光量会变大，所以先收口。
// 读接口本仓一律用 GET（pageHotspot、订单列表、queryByTickId、clip 列表等），
// 所以这条规则不会让任何读请求失去重试。
//
// 注意：这不是根治。根治要后端幂等键 —— 见 h5#7。
const IDEMPOTENT_METHODS = ['get', 'head', 'options', 'put', 'delete']

axiosRetry(base, {
  retries: 2,
  retryCondition: (error) => {
    // 自己转小写，不依赖 axios 是否已规范化 config.method ——
    // 本仓两种写法都有（method: 'POST' 与 method: 'post'）。
    const method = String(error.config?.method || '').toLowerCase()
    if (!IDEMPOTENT_METHODS.includes(method)) return false
    return axiosRetry.isNetworkOrIdempotentRequestError(error)
  },
  retryDelay: (retryCount) => {
    return retryCount * 1000
  },
  onRetry(retryCount, error, requestConfig) {
    requestConfig.__retryCount = retryCount
  },
})

let authFailedPopupShow = false
let accountFrozenPopupShow = false
function handleTip(code: HttpResponse['code']) {
  const $t = i18n.global.t
  const userStore = useUserStore()

  switch (code) {
    case 401:
      {
        if (authFailedPopupShow) return

        authFailedPopupShow = true
        usePopup({
          component: MPopup,
          props: {
            title: $t('common.authFailedPopup.title'),
            subTitle: $t('common.authFailedPopup.subtitle'),
            confirmBtnText: $t('buttonText.gotIt'),
            icon: 'error',
          },
        }).then(() => {
          authFailedPopupShow = false
          if (useGlobalStore().inMP) {
            window.uni.reLaunch({
              url: '/pages/login/index',
            })
            return
          }

          router.push({ name: PageName.LOGIN })
        })
        userStore.logout()
      }
      break
    case 4011:
      {
        if (accountFrozenPopupShow) return

        accountFrozenPopupShow = true
        usePopup({
          component: MPopup,
          props: {
            title: $t('common.accountFrozenPopup.title'),
            subTitle: $t('common.accountFrozenPopup.subtitle'),
            confirmBtnText: $t('buttonText.gotIt'),
            icon: 'error',
          },
        }).then(() => {
          accountFrozenPopupShow = false
        })
        userStore.logout()
      }
      break
  }
}

export const apiClient = base
export const apiClientV2 = baseV2
